import cx from 'classnames';
import ReactDOM from 'react-dom';
import {confirm, Dialog} from '@baidu/luffy-comp-dialog';
import {Collapse, CollapsePanel} from '@baidu/luffy-comp-collapse';
import {Switch} from '@baidu/luffy-comp-switch';
import {RangePicker} from '@baidu/luffy-comp-date-picker';

import toast, {ToastType} from '@/components/Toast';
import Button, {ButtonType} from '@/components/Button';
import Icon from '@/components/Icon';
import CloseX from '@/components/CloseX';
import Popover from '@/components/Popover';
import Tooltip, {EllipsisTooltip} from '@/components/Tooltip';
import Input from '@/components/Input';
import Select from '@/components/Select';
import Avatar from '@/components/Avatar';
import Table from '@/components/Table';
import Loading from '@/components/Loading';

import FlowCardDialog from '../components/FlowCardDialog';
import type {IComponent} from '../types';
import styles from './style.module.scss';

// 存储当前打开的弹窗实例
let dialogInstance: {
    id: string;
    container: HTMLDivElement;
} | null = null;

// 生成唯一ID
function generateId(): string {
    return `dialog_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// 销毁当前弹窗
function destroyDialog() {
    if (dialogInstance) {
        ReactDOM.unmountComponentAtNode(dialogInstance.container);
        document.body.removeChild(dialogInstance.container);
        dialogInstance = null;
    }
}

const showDialog: IComponent['showDialog'] = (options) => {
    const {
        content,
        className,
        cancelLabel = '取消',
        confirmLabel = '确定',
        onCancel,
        onConfirm,
        ...restProps
    } = options;

    // 销毁之前的弹窗
    destroyDialog();

    const container = document.createElement('div');
    document.body.appendChild(container);

    const id = generateId();
    dialogInstance = {id, container};

    const handleClose = (e: React.MouseEvent) => {
        onCancel && onCancel(e);
        destroyDialog();
    };

    ReactDOM.render(
        <Dialog
            visible
            showHeader={false}
            className={cx(styles.dialog, className)}
            onCancel={handleClose}
            osType="windows"
            actions={
                <div>
                    <Button
                        type={ButtonType.Plain}
                        border
                        shape="capsule"
                        onClick={handleClose}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        noMinWidth
                        type={ButtonType.Important}
                        shape="capsule"
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            }
            {...restProps}
        >
            {content}
        </Dialog>,
        container
    );

    return {
        destroy: destroyDialog,
    };
};

const showFlowCardDialog: IComponent['showFlowCardDialog'] = (props) => {
    showDialog({
        className: 'kuDropMenuFlowCardDialog',
        showCloseX: true,
        maskCancel: true,
        width: 630,
        content: (
            <div>
                <FlowCardDialog {...props} />
            </div>
        ),
    });
};

export const component: IComponent = {
    showConfirm: (props = {}) => {
        const {className, message, confirmLabel, cancelLabel, onConfirm, onCancel, ...restProps} = props;

        return confirm({
            className: cx(styles.confirm, className),
            message,
            confirmLabel,
            cancelLabel,
            onConfirm,
            onCancel,
            ...restProps,
        });
    },
    showToast: (props = {}) => {
        const {className, content = '', duration, type = 'normal', manualClose, onClose, ...restProps} = props;

        const typeMap = {
            success: ToastType.Success,
            warning: ToastType.Warning,
            error: ToastType.Error,
            normal: ToastType.Normal,
        };

        toast({
            className,
            content,
            duration,
            type: typeMap[type],
            manualClose,
            onClose,
            ...restProps,
        });
    },
    showDialog,
    Button,
    Icon,
    CloseX: (props: any) => <CloseX {...props} />,
    Dialog,
    Collapse,
    CollapsePanel,
    Popover: (props: any) => <Popover {...props} />,
    Tooltip,
    EllipsisTooltip,
    Input,
    Select,
    Switch,
    Avatar,
    Table,
    Loading: (props: any) => <Loading {...props} />,
    RangePicker: (props: any) => <RangePicker {...props} />,
    showFlowCardDialog,
};
